import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../utils/api';

const getMinDateTime = () => {
  const d = new Date(Date.now() + 5 * 60 * 1000);
  const pad = n => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth()+1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
};

export default function NewTicket() {
  const navigate = useNavigate();
  const [templates, setTemplates] = useState([]);
  const [form, setForm] = useState({
    patient_name: '',
    patient_email: '',
    cc: '',
    bcc: '',
    subject: '',
    message: '',
  });
  const [showCCBCC, setShowCCBCC] = useState(false);
  const [showSchedule, setShowSchedule] = useState(false);
  const [scheduleTime, setScheduleTime] = useState('');
  const [sending, setSending] = useState(false);
  const [improving, setImproving] = useState(false);
  const [attachFiles, setAttachFiles] = useState([]);
  const [error, setError] = useState('');
  const [isMobile, setIsMobile] = useState(() => typeof window !== 'undefined' && window.innerWidth < 768);

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => {
    api.get('/templates').then(({ data }) => setTemplates(data.templates || []));
  }, []);

  const handleSubmit = async (scheduledFor = null) => {
    if (!form.patient_email || !form.subject || !form.message) {
      return setError('Email, subject and message are required');
    }
    if (scheduledFor && new Date(scheduledFor) <= new Date()) {
      return setError('Scheduled time must be in the future');
    }
    setSending(true);
    setError('');
    try {
      const formData = new FormData();
      formData.append('patient_name', form.patient_name);
      formData.append('patient_email', form.patient_email);
      formData.append('cc', form.cc);
      formData.append('bcc', form.bcc);
      formData.append('subject', form.subject);
      formData.append('message', form.message);
      if (scheduledFor) formData.append('scheduled_for', scheduledFor);
      attachFiles.forEach(f => formData.append('attachments', f));
      const { data } = await api.post('/tickets/outbound', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      navigate(`/tickets/${data.ticket.id}`);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to send');
    } finally {
      setSending(false);
    }
  };

  const handleImprove = async () => {
    if (!form.message.trim()) return;
    setImproving(true);
    try {
      const { data } = await api.post('/tickets/improve-draft', {
        draft: form.message,
        patient_name: form.patient_name,
        subject: form.subject,
      });
      setForm(p => ({ ...p, message: data.improved }));
    } catch (err) {
      alert('Failed to improve message');
    } finally {
      setImproving(false);
    }
  };

  const inputStyle = {
    width: '100%', padding: '8px 10px', border: '1px solid #ddd',
    borderRadius: '6px', fontSize: '13px', boxSizing: 'border-box',
  };

  const labelStyle = {
    display: 'block', fontSize: '12px', color: '#666', marginBottom: '4px',
  };

  return (
    <div style={{ maxWidth: '680px' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '20px' }}>
        <button onClick={() => navigate(-1)}
          style={{ background: 'none', border: '1px solid #ddd', borderRadius: '6px', padding: '5px 12px', fontSize: '13px', cursor: 'pointer' }}>
          Back
        </button>
        <h2 style={{ fontSize: '18px', fontWeight: '600' }}>New email</h2>
      </div>

      {error && (
        <div style={{ background: '#FCEBEB', border: '1px solid #F09595', borderRadius: '8px', padding: '10px 14px', marginBottom: '14px', fontSize: '13px', color: '#791F1F' }}>
          {error}
        </div>
      )}

      <div style={{ background: '#fff', border: '1px solid #e8e8e8', borderRadius: '10px', padding: isMobile ? '16px' : '20px', display: 'flex', flexDirection: 'column', gap: '14px' }}>

        <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr', gap: '14px' }}>
          <div>
            <label style={labelStyle}>Patient name</label>
            <input value={form.patient_name} onChange={e => setForm(p => ({ ...p, patient_name: e.target.value }))}
              placeholder="Full name"
              style={inputStyle} />
          </div>
          <div>
            <label style={labelStyle}>To (patient email) *</label>
            <div style={{ display: 'flex', gap: '6px', alignItems: 'center' }}>
              <input type="email" value={form.patient_email} onChange={e => setForm(p => ({ ...p, patient_email: e.target.value }))}
                placeholder="patient@email.com"
                style={{ ...inputStyle, flex: 1 }} />
              {!showCCBCC && (
                <button onClick={() => setShowCCBCC(true)}
                  style={{ background: 'none', border: 'none', fontSize: '12px', color: '#185FA5', cursor: 'pointer', flexShrink: 0, whiteSpace: 'nowrap' }}>
                  CC/BCC
                </button>
              )}
            </div>
          </div>
        </div>

        {showCCBCC && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
            <div>
              <label style={labelStyle}>CC</label>
              <input value={form.cc} onChange={e => setForm(p => ({ ...p, cc: e.target.value }))}
                placeholder="cc@email.com, another@email.com"
                style={inputStyle} />
            </div>
            <div>
              <label style={labelStyle}>BCC</label>
              <input value={form.bcc} onChange={e => setForm(p => ({ ...p, bcc: e.target.value }))}
                placeholder="bcc@email.com"
                style={inputStyle} />
            </div>
          </div>
        )}

        <div>
          <label style={labelStyle}>Subject *</label>
          <input value={form.subject} onChange={e => setForm(p => ({ ...p, subject: e.target.value }))}
            placeholder="Email subject"
            style={inputStyle} />
        </div>

        {templates.length > 0 && (
          <div>
            <label style={labelStyle}>Insert a template</label>
            <select onChange={e => {
              const t = templates.find(t => t.id === e.target.value);
              if (t) setForm(p => ({ ...p, message: t.body.replace(/\{\{patient_name\}\}/g, form.patient_name?.split(' ')[0] || 'there'), ...(t.subject && !p.subject ? { subject: t.subject } : {}) }));
              e.target.value = '';
            }} style={{ ...inputStyle }}>
              <option value="">Choose a template...</option>
              {templates.map(t => (
                <option key={t.id} value={t.id}>{t.name}</option>
              ))}
            </select>
          </div>
        )}

        <div>
          <label style={labelStyle}>Message *</label>
          <textarea value={form.message} onChange={e => setForm(p => ({ ...p, message: e.target.value }))}
            placeholder="Type your message..."
            rows={8}
            style={{ ...inputStyle, fontFamily: 'inherit', resize: 'vertical' }} />
        </div>

        <div>
          <label style={labelStyle}>Attach files (max 10MB each)</label>
          <input type="file" multiple onChange={e => setAttachFiles(prev => [...prev, ...Array.from(e.target.files)])}
            style={{ fontSize: '13px' }} />
          {attachFiles.length > 0 && (
            <div style={{ fontSize: '12px', color: '#666', marginTop: '4px', display: 'flex', flexWrap: 'wrap', gap: '6px' }}>
              {attachFiles.map((f, i) => (
                <span key={i} style={{ background: '#f0f0f0', padding: '2px 8px', borderRadius: '4px', display: 'flex', alignItems: 'center', gap: '4px' }}>
                  {f.name}
                  <button onClick={() => setAttachFiles(prev => prev.filter((_, idx) => idx !== i))}
                    style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#c00', fontWeight: 'bold', fontSize: '14px', lineHeight: 1 }}>x</button>
                </span>
              ))}
            </div>
          )}
        </div>

        {showSchedule && (
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap', padding: '10px', background: '#F5F0FF', borderRadius: '8px', border: '1px solid #DDD6FE' }}>
            <span style={{ fontSize: '13px', color: '#6B21A8', fontWeight: '500' }}>Send at:</span>
            <input type="datetime-local" value={scheduleTime} onChange={e => setScheduleTime(e.target.value)}
              min={getMinDateTime()}
              style={{ padding: '6px 10px', border: '1px solid #DDD6FE', borderRadius: '6px', fontSize: '13px' }} />
            <button onClick={() => { setShowSchedule(false); setScheduleTime(''); }}
              style={{ background: 'none', border: 'none', fontSize: '12px', color: '#888', cursor: 'pointer' }}>
              Cancel
            </button>
          <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap', marginTop: '8px' }}>
            {[{ label: 'In 12 days', days: 12 }, { label: 'In 26 days', days: 26 }, { label: 'In 3 weeks', days: 21 }].map(({ label, days }) => {
              const d = new Date(); d.setDate(d.getDate() + days); d.setHours(10, 0, 0, 0);
              const pad = n => String(n).padStart(2, '0');
              const val = `${d.getFullYear()}-${pad(d.getMonth()+1)}-${pad(d.getDate())}T10:00`;
              return (
                <button key={label} onClick={() => setScheduleTime(val)}
                  style={{ padding: '4px 10px', background: scheduleTime === val ? '#6B21A8' : '#fff', color: scheduleTime === val ? '#fff' : '#6B21A8', border: '1px solid #6B21A8', borderRadius: '6px', fontSize: '12px', cursor: 'pointer' }}>
                  {label}
                </button>
              );
            })}
          </div>
          </div>
        )}

        <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap', alignItems: 'center' }}>
          <button onClick={handleImprove} disabled={improving || !form.message.trim()}
            style={{ flex: isMobile ? '1 1 100%' : '0 0 auto', padding: '9px 16px', background: (improving || !form.message.trim()) ? '#ddd' : '#0F766E', color: '#fff', border: 'none', borderRadius: '8px', fontSize: '13px', fontWeight: '500', cursor: (improving || !form.message.trim()) ? 'not-allowed' : 'pointer' }}>
            {improving ? 'Improving...' : 'AI improve'}
          </button>
          <button onClick={() => handleSubmit(null)} disabled={sending}
            style={{ flex: isMobile ? '1' : '0 0 auto', padding: '9px 20px', background: '#185FA5', color: '#fff', border: 'none', borderRadius: '8px', fontSize: '13px', fontWeight: '500', cursor: sending ? 'not-allowed' : 'pointer', opacity: sending ? 0.7 : 1 }}>
            {sending ? 'Sending...' : 'Send email'}
          </button>

          {showSchedule ? (
            <button onClick={() => {
              if (!scheduleTime) return setError('Please pick a date and time');
              handleSubmit(scheduleTime);
            }} disabled={sending || !scheduleTime}
              style={{ flex: isMobile ? '1' : '0 0 auto', padding: '9px 16px', background: (sending || !scheduleTime) ? '#ddd' : '#6B21A8', color: '#fff', border: 'none', borderRadius: '8px', fontSize: '13px', fontWeight: '500', cursor: (sending || !scheduleTime) ? 'not-allowed' : 'pointer' }}>
              {sending ? 'Scheduling...' : 'Schedule send'}
            </button>
          ) : (
            <button onClick={() => { setShowSchedule(true); if (!scheduleTime) { const d = new Date(); d.setDate(d.getDate() + 1); d.setHours(10, 0, 0, 0); const pad = n => String(n).padStart(2, '0'); setScheduleTime(`${d.getFullYear()}-${pad(d.getMonth()+1)}-${pad(d.getDate())}T10:00`); } }}
              style={{ flex: isMobile ? '1' : '0 0 auto', padding: '9px 16px', background: 'none', border: '1px solid #ddd', borderRadius: '8px', fontSize: '13px', color: '#555', cursor: 'pointer' }}>
              Send later
            </button>
          )}

          <button onClick={() => navigate(-1)}
            style={{ flex: isMobile ? '1' : '0 0 auto', padding: '9px 16px', background: '#fff', border: '1px solid #ddd', borderRadius: '8px', fontSize: '13px', cursor: 'pointer' }}>
            Cancel
          </button>
        </div>
      </div>
    </div>
  );
}
